import { useCallback, useEffect, useMemo, useState } from 'react';
import { client } from '../../amplify-client';
import { AdressSuche } from '../../components/AdressSuche';
import { MiniKarte } from '../../components/MiniKarte';
import { findeMoeglicheDublette } from '../../lib/dublettenCheck';
import { parseJson } from '../../lib/json';
import type { Schema } from '../../../amplify/data/resource';

type Eisdiele = Schema['Eisdiele']['type'];

type Formular = {
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  region: string;
  kette: string;
  website: string;
  notizen: string;
  lat: number;
  lng: number;
};

function formularAus(e: Eisdiele): Formular {
  return {
    name: e.name,
    strasse: e.strasse,
    plz: e.plz,
    ort: e.ort,
    region: e.region ?? '',
    kette: e.kette ?? '',
    website: e.website ?? '',
    notizen: e.notizen ?? '',
    lat: e.lat,
    lng: e.lng,
  };
}

/**
 * Verwaltung → Eisdielen (Bauphase 7): Stammdaten ändern (inkl. Geo-Pin
 * korrigieren) und Dubletten zusammenführen — siehe functional-design.md 4.1
 * ("Verwalter können zwei Einträge zusammenführen") und die Zeile
 * "Eisdielen-Stammdaten ändern/zusammenführen/löschen" in der Rechte-Tabelle.
 *
 * Stammdaten-Änderungen laufen direkt über `client.models.Eisdiele.update()`
 * — keine eigene Mutation nötig, `allow.group('Verwalter')` im Datenschema
 * gibt Verwaltern bereits volles CRUD auf das Modell. Nur das Zusammenführen
 * braucht Server-Logik (Tests umhängen + Quelle löschen), siehe
 * `eisdielenZusammenfuehren` in amplify/functions/verkostung-schreiben.
 */
export function VerwaltungEisdielen() {
  const [eisdielen, setEisdielen] = useState<Eisdiele[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [bearbeiteId, setBearbeiteId] = useState<string | null>(null);
  const [formular, setFormular] = useState<Formular | null>(null);
  const [speichern, setSpeichern] = useState(false);
  const [loeschId, setLoeschId] = useState<string | null>(null);

  const [quelleId, setQuelleId] = useState('');
  const [zielId, setZielId] = useState('');
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeFehler, setMergeFehler] = useState<string | null>(null);
  const [mergeErfolg, setMergeErfolg] = useState<string | null>(null);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    client.models.Eisdiele.list({ limit: 2000 })
      .then(({ data, errors }) => {
        if (errors?.length) throw new Error(errors[0].message);
        setEisdielen([...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'de')));
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, []);

  useEffect(() => laden(), [laden]);

  const dublette = useMemo(() => {
    if (!formular) return null;
    const andere = eisdielen.filter((e) => e.id !== bearbeiteId);
    return findeMoeglicheDublette(formular.lat, formular.lng, andere);
  }, [formular, eisdielen, bearbeiteId]);

  function bearbeitenOeffnen(e: Eisdiele) {
    setBearbeiteId(e.id);
    setFormular(formularAus(e));
    setFehler(null);
  }

  async function stammdatenSpeichern() {
    if (!bearbeiteId || !formular) return;
    setSpeichern(true);
    setFehler(null);
    try {
      const { errors } = await client.models.Eisdiele.update({
        id: bearbeiteId,
        name: formular.name,
        strasse: formular.strasse,
        plz: formular.plz,
        ort: formular.ort,
        region: formular.region || undefined,
        kette: formular.kette || undefined,
        website: formular.website || undefined,
        notizen: formular.notizen || undefined,
        lat: formular.lat,
        lng: formular.lng,
      });
      if (errors?.length) throw new Error(errors[0].message);
      setBearbeiteId(null);
      setFormular(null);
      laden();
    } catch (e) {
      setFehler(String(e instanceof Error ? e.message : e));
    } finally {
      setSpeichern(false);
    }
  }

  async function eisdieleLoeschen(e: Eisdiele) {
    if (!confirm(`„${e.name}" (${e.ort}) wirklich unwiderruflich löschen?`)) return;
    setLoeschId(e.id);
    setFehler(null);
    try {
      const { errors } = await client.models.Eisdiele.delete({ id: e.id });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (err) {
      setFehler(String(err instanceof Error ? err.message : err));
    } finally {
      setLoeschId(null);
    }
  }

  async function zusammenfuehren() {
    setMergeFehler(null);
    setMergeErfolg(null);
    if (!quelleId || !zielId) {
      setMergeFehler('Bitte Quelle und Ziel auswählen.');
      return;
    }
    if (quelleId === zielId) {
      setMergeFehler('Quelle und Ziel müssen unterschiedlich sein.');
      return;
    }
    const quelle = eisdielen.find((e) => e.id === quelleId);
    const ziel = eisdielen.find((e) => e.id === zielId);
    if (!confirm(`„${quelle?.name}" wird gelöscht, alle ihre Verkostungen wandern zu „${ziel?.name}". Fortfahren?`)) return;

    setMergeBusy(true);
    try {
      const { data, errors } = await client.mutations.eisdielenZusammenfuehren({ quelleId, zielId });
      if (errors?.length) throw new Error(errors[0].message);
      const ergebnis = parseJson<{ verschobeneTests?: number } | null>(data);
      setMergeErfolg(
        `„${quelle?.name}" wurde gelöscht, ${ergebnis?.verschobeneTests ?? 0} Verkostung(en) sind jetzt bei „${ziel?.name}".`,
      );
      setQuelleId('');
      setZielId('');
      laden();
    } catch (e) {
      setMergeFehler(String(e instanceof Error ? e.message : e));
    } finally {
      setMergeBusy(false);
    }
  }

  return (
    <div>
      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <h2>Dubletten zusammenführen</h2>
        <p className="hinweis">Alle Verkostungen der Quelle wandern zum Ziel, danach wird die Quelle gelöscht.</p>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 14rem' }}>
            <label htmlFor="merge-quelle">Quelle (wird gelöscht)</label>
            <select id="merge-quelle" value={quelleId} onChange={(e) => setQuelleId(e.target.value)}>
              <option value="">– bitte wählen –</option>
              {eisdielen.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.ort}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 14rem' }}>
            <label htmlFor="merge-ziel">Ziel (bleibt bestehen)</label>
            <select id="merge-ziel" value={zielId} onChange={(e) => setZielId(e.target.value)}>
              <option value="">– bitte wählen –</option>
              {eisdielen.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.ort}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primaer" disabled={mergeBusy} onClick={zusammenfuehren}>
            Zusammenführen
          </button>
        </div>
        {mergeFehler ? (
          <p className="hinweis" style={{ borderColor: 'var(--erdbeere)', marginTop: '0.75rem' }}>
            {mergeFehler}
          </p>
        ) : null}
        {mergeErfolg ? <p className="hinweis" style={{ marginTop: '0.75rem' }}>{mergeErfolg}</p> : null}
      </section>

      <h2>Alle Eisdielen</h2>
      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? (
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          {fehler}
        </p>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
        {eisdielen.map((e) => (
          <div key={e.id} className="karte">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{e.name}</div>
                <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>
                  {e.strasse}, {e.plz} {e.ort} · {e.anzahlTests ?? 0} Verkostung{(e.anzahlTests ?? 0) === 1 ? '' : 'en'}
                  {e.lat === 0 && e.lng === 0 ? ' · ⚠ keine Position' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-sekundaer" onClick={() => bearbeitenOeffnen(e)}>
                  {bearbeiteId === e.id ? 'Bearbeiten (offen)' : 'Bearbeiten'}
                </button>
                <button
                  className="btn btn-sekundaer"
                  disabled={loeschId === e.id || (e.anzahlTests ?? 0) > 0}
                  title={(e.anzahlTests ?? 0) > 0 ? 'Hat noch Verkostungen — erst zusammenführen oder deren Verkostungen entfernen.' : undefined}
                  onClick={() => eisdieleLoeschen(e)}
                  style={{ color: 'var(--erdbeere)' }}
                >
                  Löschen
                </button>
              </div>
            </div>

            {bearbeiteId === e.id && formular ? (
              <div
                style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid var(--raspel)',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: '0.6rem',
                }}
              >
                <AdressSuche
                  onAuswahl={(t) =>
                    setFormular((f) =>
                      f
                        ? {
                            ...f,
                            strasse: t.strasse || f.strasse,
                            plz: t.plz || f.plz,
                            ort: t.ort || f.ort,
                            region: t.region || f.region,
                            lat: t.lat,
                            lng: t.lng,
                          }
                        : f,
                    )
                  }
                />
                <div>
                  <label>Name</label>
                  <input value={formular.name} onChange={(ev) => setFormular({ ...formular, name: ev.target.value })} />
                </div>
                <div>
                  <label>Straße & Hausnummer</label>
                  <input value={formular.strasse} onChange={(ev) => setFormular({ ...formular, strasse: ev.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '0 0 8rem' }}>
                    <label>PLZ</label>
                    <input value={formular.plz} onChange={(ev) => setFormular({ ...formular, plz: ev.target.value })} />
                  </div>
                  <div style={{ flex: '1 1 10rem' }}>
                    <label>Ort</label>
                    <input value={formular.ort} onChange={(ev) => setFormular({ ...formular, ort: ev.target.value })} />
                  </div>
                </div>

                <MiniKarte
                  lat={formular.lat}
                  lng={formular.lng}
                  onChange={(lat, lng) => setFormular((f) => (f ? { ...f, lat, lng } : f))}
                />
                {dublette ? (
                  <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
                    Ganz in der Nähe (≤ 120 m) gibt es schon <strong>{dublette.name}</strong> ({dublette.ort}) — vielleicht
                    eher zusammenführen als beide getrennt pflegen?
                  </p>
                ) : null}

                <div>
                  <label>Kette / Filiale (optional)</label>
                  <input value={formular.kette} onChange={(ev) => setFormular({ ...formular, kette: ev.target.value })} />
                </div>
                <div>
                  <label>Website (optional)</label>
                  <input value={formular.website} onChange={(ev) => setFormular({ ...formular, website: ev.target.value })} />
                </div>
                <div>
                  <label>Notizen (optional)</label>
                  <textarea value={formular.notizen} onChange={(ev) => setFormular({ ...formular, notizen: ev.target.value })} />
                </div>

                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <button className="btn btn-primaer" disabled={speichern} onClick={stammdatenSpeichern}>
                    Speichern
                  </button>
                  <button
                    className="btn btn-sekundaer"
                    disabled={speichern}
                    onClick={() => {
                      setBearbeiteId(null);
                      setFormular(null);
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
